import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const InverterControlToggle: React.FC = () => {
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiService.getSettings(),
  });

  const updateSettingMutation = useMutation({
    mutationFn: (value: boolean) =>
      apiService.updateSetting('INVERTER_CONTROL_ENABLED', { value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Inverter Control Enable setting updated');
    },
    onError: (error: any) => {
      console.error('Error updating setting:', error);
      toast.error('Failed to update setting');
    },
    onSettled: () => {
      setIsUpdating(false);
    },
  });

  const handleToggle = async () => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    const currentValue = settings?.settings?.INVERTER_CONTROL_ENABLED ?? true;
    updateSettingMutation.mutate(!currentValue);
  };

  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-xs text-gray-500">
        <Settings className="h-3 w-3" />
        <span>Loading...</span>
      </div>
    );
  }

  const isEnabled = settings?.settings?.INVERTER_CONTROL_ENABLED ?? true;

  return (
    <div className="w-full flex items-center justify-between px-3 py-2 text-xs bg-gray-50 rounded-md border border-gray-200">
      <div className="flex items-center space-x-2">
        <Settings className="h-3 w-3 text-gray-500" />
        <span className="text-gray-700">Enable Inverter Control</span>
      </div>
      <button
        onClick={handleToggle}
        disabled={isUpdating}
        className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
          isEnabled ? 'bg-primary-600' : 'bg-gray-200'
        } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
            isEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
};

export default InverterControlToggle;
